import React from 'react';
import { Award as LucideAward, Clock as LucideClock, HeartHandshake as LucideHeartHandshake, Rocket as LucideRocket, ShieldCheck as LucideShieldCheck, Zap as LucideZap, Headphones as LucideHeadphones, Code2 as LucideCode2, Search as LucideSearch, MapPin as LucideMapPin, Palette as LucidePalette, TestTube as LucideTestTube, GitBranch as LucideGitBranch, ExternalLink as LucideExternalLink, Layers as LucideLayers, ChevronLeft as LucideChevronLeft, ChevronRight as LucideChevronRight, Quote as LucideQuote, MessageSquare as LucideMessageSquare, Globe as LucideGlobe, Wrench as LucideWrench, Smartphone as LucideSmartphone, Database as LucideDatabase, Users as LucideUsers, Cloud as LucideCloud, Server as LucideServer, Layout as LucideLayout, Bot as LucideBot, Link2 as LucideLink2, ArrowUpRight as LucideArrowUpRight, Heart as LucideHeart, Send as LucideSend, Mail as LucideMail, Phone as LucidePhone, ArrowRight as LucideArrowRight, CheckCircle as LucideCheckCircle, Check as LucideCheck, Star as LucideStar, CreditCard as LucideCreditCard, Play as LucidePlay } from 'lucide-react';
import SketchAward from 'sketch-icons/dist/simple/icons/Award';
import SketchClock from 'sketch-icons/dist/simple/icons/Clock';
import SketchShieldCheck from 'sketch-icons/dist/simple/icons/ShieldCheck';
import SketchHeadset from 'sketch-icons/dist/simple/icons/Headset';
import SketchCodeAlt from 'sketch-icons/dist/simple/icons/CodeAlt';
import SketchSearch from 'sketch-icons/dist/simple/icons/Search';
import SketchLocationMarker from 'sketch-icons/dist/simple/icons/LocationMarker';
import SketchColorPalette from 'sketch-icons/dist/simple/icons/ColorPalette';
import SketchGit from 'sketch-icons/dist/simple/icons/Git';
import SketchExternalLink from 'sketch-icons/dist/simple/icons/ExternalLink';
import SketchCategory from 'sketch-icons/dist/simple/icons/Category';
import SketchBlockquoteLeft from 'sketch-icons/dist/simple/icons/BlockquoteLeft';
import SketchChat from 'sketch-icons/dist/simple/icons/Chat';
import SketchGlobalNetwork from 'sketch-icons/dist/simple/icons/GlobalNetwork';
import SketchSettings from 'sketch-icons/dist/simple/icons/Settings';
import SketchMobile from 'sketch-icons/dist/simple/icons/Mobile';
import SketchPeople from 'sketch-icons/dist/simple/icons/People';
import SketchHeart from 'sketch-icons/dist/simple/icons/Heart';
import SketchSent from 'sketch-icons/dist/simple/icons/Sent';
import SketchAlternateEmail from 'sketch-icons/dist/simple/icons/AlternateEmail';
import SketchPhone from 'sketch-icons/dist/simple/icons/Phone';
import SketchArrowRight from 'sketch-icons/dist/simple/icons/ArrowRight';
import SketchStar from 'sketch-icons/dist/simple/icons/Star';
import SketchPlay from 'sketch-icons/dist/simple/icons/Play';

export interface SketchIconProps extends React.ComponentPropsWithoutRef<'svg'> {
  size?: number | string;
  color?: string;
  strokeWidth?: number | string;
}

// Higher-order component to standardise Lucide and Sketch-icons props without heavy SVG filters
function createSketchIcon(
  SketchComponent?: React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number | string }>,
  LucideFallback?: React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number | string }>
) {
  const IconComponent = React.forwardRef<SVGSVGElement, SketchIconProps>(
    ({ size = 24, color = 'currentColor', strokeWidth = 2, className = '', style, ...props }, ref) => {
      const parsedSize = typeof size === 'number' ? `${size}px` : size;

      const combinedStyle: React.CSSProperties = {
        width: parsedSize,
        height: parsedSize,
        display: 'inline-block',
        verticalAlign: 'middle',
        ...style,
      };

      // Prefer standard Lucide icons for lag-free modern look
      if (LucideFallback) {
        const Fallback = LucideFallback;
        return (
          <Fallback
            ref={ref}
            size={size}
            color={color}
            strokeWidth={strokeWidth}
            className={`sketch-icon-pack-svg ${className}`}
            style={combinedStyle}
            {...props}
          />
        );
      }

      // Render sketch-icons SVG component if no Lucide fallback
      if (SketchComponent) {
        const Comp = SketchComponent;
        return (
          <Comp
            ref={ref}
            width={size}
            height={size}
            color={color}
            strokeWidth={strokeWidth}
            className={`sketch-icon-pack-svg ${className}`}
            style={combinedStyle}
            {...props}
          />
        );
      }

      return null;
    }
  );

  IconComponent.displayName = 'SketchIcon';
  return IconComponent;
}

// 1. WhyChooseUs Icons
export const Award = createSketchIcon(SketchAward, LucideAward);
export const Clock = createSketchIcon(SketchClock, LucideClock);
export const HeartHandshake = createSketchIcon(undefined, LucideHeartHandshake); // Needs Lucide shape
export const Rocket = createSketchIcon(undefined, LucideRocket); // Needs Lucide shape
export const ShieldCheck = createSketchIcon(SketchShieldCheck, LucideShieldCheck);
export const Zap = createSketchIcon(undefined, LucideZap); // Needs Lucide shape
export const Headphones = createSketchIcon(SketchHeadset, LucideHeadphones);
export const Code2 = createSketchIcon(SketchCodeAlt, LucideCode2);

// 2. Process Icons
export const Search = createSketchIcon(SketchSearch, LucideSearch);
export const MapPin = createSketchIcon(SketchLocationMarker, LucideMapPin);
export const Palette = createSketchIcon(SketchColorPalette, LucidePalette);
export const TestTube = createSketchIcon(undefined, LucideTestTube);
export const GitBranch = createSketchIcon(SketchGit, LucideGitBranch);

// 3. Portfolio Icons
export const ExternalLink = createSketchIcon(SketchExternalLink, LucideExternalLink);
export const Layers = createSketchIcon(SketchCategory, LucideLayers);

// 4. Testimonials Icons
export const ChevronLeft = createSketchIcon(undefined, LucideChevronLeft);
export const ChevronRight = createSketchIcon(undefined, LucideChevronRight);
export const Quote = createSketchIcon(SketchBlockquoteLeft, LucideQuote);
export const MessageSquare = createSketchIcon(SketchChat, LucideMessageSquare);

// 5. Services & Tech Stack Icons
export const Globe = createSketchIcon(SketchGlobalNetwork, LucideGlobe);
export const Wrench = createSketchIcon(SketchSettings, LucideWrench);
export const Smartphone = createSketchIcon(SketchMobile, LucideSmartphone);
export const Database = createSketchIcon(undefined, LucideDatabase);
export const Users = createSketchIcon(SketchPeople, LucideUsers);
export const Cloud = createSketchIcon(undefined, LucideCloud);
export const Server = createSketchIcon(undefined, LucideServer);
export const Layout = createSketchIcon(undefined, LucideLayout);
export const Bot = createSketchIcon(undefined, LucideBot);
export const Link2 = createSketchIcon(undefined, LucideLink2);

// 6. Footer Icons
export const ArrowUpRight = createSketchIcon(undefined, LucideArrowUpRight);
export const Heart = createSketchIcon(SketchHeart, LucideHeart);

// 7. Contact Icons
export const Send = createSketchIcon(SketchSent, LucideSend);
export const Mail = createSketchIcon(SketchAlternateEmail, LucideMail);
export const Phone = createSketchIcon(SketchPhone, LucidePhone);
export const ArrowRight = createSketchIcon(SketchArrowRight, LucideArrowRight);
export const CheckCircle = createSketchIcon(undefined, LucideCheckCircle);

// 8. Pricing Icons
export const Check = createSketchIcon(undefined, LucideCheck);
export const Star = createSketchIcon(SketchStar, LucideStar);
export const CreditCard = createSketchIcon(undefined, LucideCreditCard);

// 9. Hero Icons
export const Play = createSketchIcon(SketchPlay, LucidePlay);

// 10. Social Icons
export const Twitter = React.forwardRef<SVGSVGElement, SketchIconProps>(
  ({ size = 24, color = 'currentColor', strokeWidth = 2, className = '', style, ...props }, ref) => {
    const parsedSize = typeof size === 'number' ? `${size}px` : size;
    return (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width={parsedSize} height={parsedSize} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={`sketch-icon-pack-svg ${className}`} style={{ display: 'inline-block', verticalAlign: 'middle', ...style }} {...props}>
        <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
      </svg>
    )
  }
)
Twitter.displayName = 'Twitter';

export const Linkedin = React.forwardRef<SVGSVGElement, SketchIconProps>(
  ({ size = 24, color = 'currentColor', strokeWidth = 2, className = '', style, ...props }, ref) => {
    const parsedSize = typeof size === 'number' ? `${size}px` : size;
    return (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width={parsedSize} height={parsedSize} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={`sketch-icon-pack-svg ${className}`} style={{ display: 'inline-block', verticalAlign: 'middle', ...style }} {...props}>
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
        <rect x="2" y="9" width="4" height="12"></rect>
        <circle cx="4" cy="4" r="2"></circle>
      </svg>
    )
  }
)
Linkedin.displayName = 'Linkedin';

export const Github = React.forwardRef<SVGSVGElement, SketchIconProps>(
  ({ size = 24, color = 'currentColor', strokeWidth = 2, className = '', style, ...props }, ref) => {
    const parsedSize = typeof size === 'number' ? `${size}px` : size;
    return (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width={parsedSize} height={parsedSize} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={`sketch-icon-pack-svg ${className}`} style={{ display: 'inline-block', verticalAlign: 'middle', ...style }} {...props}>
        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
      </svg>
    )
  }
)
Github.displayName = 'Github';

export const Instagram = React.forwardRef<SVGSVGElement, SketchIconProps>(
  ({ size = 24, color = 'currentColor', strokeWidth = 2, className = '', style, ...props }, ref) => {
    const parsedSize = typeof size === 'number' ? `${size}px` : size;
    return (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width={parsedSize} height={parsedSize} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={`sketch-icon-pack-svg ${className}`} style={{ display: 'inline-block', verticalAlign: 'middle', ...style }} {...props}>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
      </svg>
    )
  }
)
Instagram.displayName = 'Instagram';
